import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiFetch } from '@shared/api/http'
import { ReceivingPeriodSchema } from '@shared/contracts/enums'
import type { Currency } from '@shared/contracts/enums'
import { Money, MonetaryValueSchema } from '@shared/lib/money/money'
import type { MonetaryValue } from '@shared/lib/money/money'

export const FeePreviewSchema = z.object({
  planId: z.string(),
  mode: z.enum(['STANDARD', 'MICRO']),
  scope: z.enum(['PIX', 'NATIONAL', 'INTERNATIONAL']),
  receivingPeriod: ReceivingPeriodSchema.optional(),
  percentage: z.string(),
  amount: MonetaryValueSchema,
  fixedFee: MonetaryValueSchema,
  percentageFee: MonetaryValueSchema,
  totalFee: MonetaryValueSchema,
  netAmount: MonetaryValueSchema,
  standardFee: MonetaryValueSchema,
  minimumSellablePrice: MonetaryValueSchema,
})

export type FeePreview = z.infer<typeof FeePreviewSchema>

export interface AdminFeePreviewInput {
  readonly producerId: string
  readonly amount: string
  readonly currency: Currency
}

export function adminFeePreviewQuery(input: AdminFeePreviewInput, enabled = true) {
  return queryOptions({
    queryKey: ['admin-fee-preview', input] as const,
    queryFn: async (): Promise<FeePreview> => {
      const search = new URLSearchParams({
        producerId: input.producerId,
        amount: input.amount,
        currency: input.currency,
        paymentMethod: 'CREDIT_CARD',
      })
      const data = await apiFetch<unknown>(`/admin/fees/preview?${search.toString()}`)
      return FeePreviewSchema.parse(data)
    },
    enabled,
    staleTime: 30_000,
    retry: 1,
  })
}

export function feePreviewMatchesAmount(
  preview: FeePreview | undefined,
  amount: MonetaryValue,
): preview is FeePreview {
  return (
    preview !== undefined &&
    preview.amount.currency === amount.currency &&
    preview.amount.amount === Money.fromMonetaryValue(amount).toAmountString()
  )
}

export function usableFeePreview(
  preview: FeePreview | undefined,
  amount: MonetaryValue,
  hasError = false,
): FeePreview | undefined {
  return !hasError && feePreviewMatchesAmount(preview, amount) ? preview : undefined
}

export async function resolveFeePreviewForSave(
  preview: FeePreview | undefined,
  amount: MonetaryValue,
  hasError: boolean,
  fetchLatest: () => Promise<FeePreview>,
): Promise<FeePreview | undefined> {
  const currentPreview = usableFeePreview(preview, amount, hasError)
  if (currentPreview) return currentPreview

  try {
    return usableFeePreview(await fetchLatest(), amount)
  } catch {
    return undefined
  }
}
